import React, { useEffect } from 'react';
import { Asset } from '../../apiTypes/GameService';
import { appendParameters } from '@SharedScripts/Utils/Url';
import LazyLiveStream from '../LiveStream/LazyLiveStream';

export interface GameGraphicsProps {
    className?: string;
    backgroundUrl?: string;
    backgroundStyle?: string;
    foregroundUrl?: string;
    foregroundStyle?: string;
    disabled?: boolean;
    isLoading?: boolean;
    error?: boolean;
    disableCache?: boolean
    useDarkGradient?: boolean;
    useLogoAsForeground?: boolean;
    useThumbnailAsBackground?: boolean;
    rounded?: string;
    onLoaded?: () => void;
    onError?: () => void;
    liveStreamLink?: string;
    assets?: Asset[];
    logoUrl?: string;
    portraitUrl?: string;
    landscapeUrl?: string;
    thumbnailUrl?: string;
    lazyLoad?: boolean;
    imageWidth?: number;
}

export const GameGraphics = ({
  backgroundUrl,
  foregroundUrl,
  logoUrl,
  thumbnailUrl,
  disabled = false,
  isLoading = false,
  error = false,
  useDarkGradient = false,
  useLogoAsForeground = false,
  useThumbnailAsBackground = false,
  foregroundStyle = '',
  backgroundStyle = '',
  rounded = 'rounded-32',
  onLoaded,
  onError,
  liveStreamLink,
  className = '',
  disableCache,
  lazyLoad,
  imageWidth
}: GameGraphicsProps) => {
  const showFallbackImage = isLoading || error || (!backgroundUrl && !liveStreamLink);

  const lightGradient = `
    linear-gradient(0deg, rgba(215, 220, 230, 0.80) 0%, rgba(215, 220, 230, 0.80) 100%),
    linear-gradient(0deg, #D7DCE6 0%, #D7DCE6 100%),
    url(${backgroundUrl}) lightgray 50% / cover no-repeat
  `;

  const darkGradient = `
    linear-gradient(0deg, rgba(40, 40, 40, 0.80) 0%,
    rgba(40, 40, 40, 0.80) 100%),
    url(${backgroundUrl}) lightgray 50% / cover no-repeat
  `;

  const handleError = () => {
    onError?.();
    onLoaded?.();
  };

  let backgroundImageUrl = useThumbnailAsBackground ? thumbnailUrl : backgroundUrl;
  let foregroundImageUrl = useLogoAsForeground ? logoUrl : foregroundUrl;
  const backgroundParams: Array<string[]> = [];
  const foregroundParams: Array<string[]> = [];
  if (disableCache) backgroundParams.push(['noCache', Date.now().toString()]);
  if (imageWidth) {
    backgroundParams.push(['w', imageWidth.toString()]);
    foregroundParams.push(['w', imageWidth.toString()]);
  }
  if (backgroundParams.length && backgroundImageUrl) backgroundImageUrl = appendParameters(backgroundParams, backgroundImageUrl);
  if (foregroundParams.length && foregroundImageUrl) foregroundImageUrl = appendParameters(foregroundParams, foregroundImageUrl);

  useEffect(() => {
    if (!backgroundImageUrl) onLoaded?.();
  }, [backgroundImageUrl]);

  return (
    <div
      className={`absolute size-full flex justify-center items-center ${showFallbackImage ? 'bg-neutrals-coolGrey-200' : ''}
        ${disabled ? 'overflow-hidden' : ''} ${isLoading ? 'skeleton animate-shimmer' : ''} ${className} ${rounded}`}
    >

      {backgroundImageUrl && <div className={`relative size-full overflow-hidden ${rounded}`}>
        {liveStreamLink &&
                    <LazyLiveStream
                      url={liveStreamLink}
                      className={`absolute ${backgroundStyle}`}
                      onError={handleError}
                      onLoad={onLoaded}
                      style={{ display: showFallbackImage ? 'none' : 'block' }} />}
        {!liveStreamLink &&
                    <img
                      src={`${backgroundImageUrl}`}
                      className={`size-full object-cover ${backgroundStyle}`}
                      onError={handleError}
                      onLoad={onLoaded}
                      loading={lazyLoad ? 'lazy' : 'eager'}
                      alt=''
                    />
        }
      </div>}

      {!liveStreamLink && foregroundImageUrl && <img
        src={foregroundImageUrl}
        className={`absolute top-0 left-0 size-full object-contain bg-clip-content ${foregroundStyle}`}
        onError={handleError}
        loading={lazyLoad ? 'lazy' : 'eager'}
        alt=''
      />}

      {disabled && <div
        className={`absolute bottom-0 left-0 size-full !bg-top ${rounded} ${!useDarkGradient ? 'opacity-80' : ''}`}
        style={{
          background: useDarkGradient ? darkGradient : lightGradient,
          backgroundBlendMode: useDarkGradient ? '' : 'normal, color, normal',
        }}
      />}

    </div>
  );
};
